// The evidence a judge actually reads.
//
// Every stage here has drifted toward handing a model a summary — a skeleton, a
// count, a row of dimensions — because summaries are easy to produce and easy to
// rank. But a line like
//
//   37  1/1  remote  prod  .  C  shell  kubernetes:run:pg-debug
//
// contains nothing decidable. A model reading it can only guess, and it will guess
// fluently, which is worse than refusing. The worst call this pipeline ever made
// was dismissing a live investigation from a count of 53; three raw commands
// reversed it instantly.
//
// So a candidate is rendered in FULL: what the author says the capability does,
// several complete calls, and — the part every earlier payload omitted — what came
// BACK. The result is usually where the interest lives. `kubectl get pods`
// returning three healthy pods and the same command returning CrashLoopBackOff are
// the same skeleton, the same dimensions, and completely different acts.
//
// Aggregates still appear, but as one labelled line of context, explicitly ranked
// below the evidence. If the two disagree the evidence wins, and the judge is told
// so.

import { redactPaths } from "../mine/skeleton.js";

const clean = (s) => String(s ?? "").replace(/\r/g, "");

/**
 * Truncate an output without losing the part that says what happened.
 *
 * Exported because a caller that caps an output BEFORE the judge sees it — a
 * corpus builder holding thousands of them in memory — would otherwise cap from
 * the front and destroy the tail this function exists to preserve, leaving the
 * judge's own head-and-tail clip nothing left to save.
 */
export function capOutput(text, max) {
  return clip(text, max, "result");
}

function clip(text, max, label) {
  const s = clean(text);
  if (s.length <= max) return s;
  // Head AND tail: a truncated-from-the-front result hides the error, and errors
  // are at the end of almost every command's output.
  const head = Math.floor(max * 0.65);
  const tail = max - head;
  return `${s.slice(0, head)}\n… [${s.length - max} chars of ${label} elided] …\n${s.slice(-tail)}`;
}

/**
 * One candidate, rendered so a judge can decide from it alone.
 *
 * @param {object} cand
 *   act         {key, system, verb, resource, reach, environments, doors, mutating, credentialed, sessions, endpoints, count}
 *   capability  optional {name, description, params, declaredReadOnly} — the tool
 *               author's own account, which beats anything inferred
 *   instances   [{command|input, output, sessionId, endpointId}] complete calls
 * @param {object} opts
 *   instances   how many to show (default 5). Below 3 a judge is guessing from
 *               one example, which is the failure this whole module exists to stop.
 *   inChars/outChars  per-instance budgets.
 */
export function renderCandidate(cand, { instances = 5, inChars = 1200, outChars = 900, redact = true } = {}) {
  const a = cand.act || {};
  const shown = (cand.instances || []).slice(0, instances);
  const lines = [];

  lines.push(`### ${a.key || cand.key}`);

  // ONLY when the tool name IS the act. A shell act's carrier is Bash, whose
  // description is 1500 characters about heredocs and git — irrelevant to whether
  // exec-ing into a production pod is interesting, and long enough to crowd out
  // the evidence that is. For shell the commands are the description.
  if (cand.capability?.description && a.door !== "shell") {
    // The strongest evidence available and the cheapest: the person who built the
    // tool wrote down what it does, in the vocabulary of the system it acts on.
    lines.push("", "what the tool's author says it does:",
      `  ${cand.capability.description}`,
      cand.capability.params?.length ? `  parameters: ${cand.capability.params.join(", ")}` : null,
      cand.capability.declaredReadOnly ? "  the author declares this read-only" : null);
  } else if (cand.capability?.description && a.door === "shell") {
    lines.push("", `carried by \`${cand.capability.name}\`; the act is the command, so read the calls.`);
  }

  lines.push("", "context (NOT evidence — weigh the calls below over these):",
    `  ${a.count ?? shown.length} calls · ${a.sessions ?? "?"} sessions · ${a.endpoints ?? "?"} endpoints`
    + `${a.doors ? ` · via ${Object.entries(a.doors).map(([d, n]) => `${d}:${n}`).join(", ")}` : ""}`,
    `  reach ${a.reach || "?"}${a.environments?.length ? ` · names ${a.environments.join(", ")}` : ""}`
    + `${a.mutating ? " · verb is mutating" : ""}${a.credentialed ? " · credentials appear in the call" : ""}`);

  lines.push("", renderEvidence(shown, { inChars, outChars, redact }));

  return lines.filter((l) => l !== null).join("\n");
}

/**
 * The calls and their results, as a judge reads them.
 *
 * Exported so every judging path renders "what came back" the same way. There are
 * two paths into a verdict — mined acts and clustered calls — and while only one of
 * them showed results, the other's judge narrated a multi-stage workflow off script
 * filenames and named six fields none of its evidence contained.
 */
export function renderEvidence(instances, { inChars = 1200, outChars = 900, redact = true } = {}) {
  const shown = instances || [];
  const lines = [`evidence — ${shown.length} complete call${shown.length === 1 ? "" : "s"}, with what came back:`];
  shown.forEach((inst, i) => {
    const sent = inst.command != null
      ? clean(inst.command)
      : JSON.stringify(inst.input ?? {}, null, 1);
    lines.push("", `[${i + 1}] sent:`,
      indent(clip(redact ? redactPaths(sent) : sent, inChars, "call")));
    const got = clean(inst.output);
    lines.push(got
      // An absent result is a fact about the trace, not an empty string. Saying so
      // stops a judge reading silence as "the call did nothing".
      ? `    got:\n${indent(clip(redact ? redactPaths(got) : got, outChars, "result"), 6)}`
      // An error carrying no body is still worth separating from an uncaptured one:
      // the first says the call failed, the second says the trace did not look.
      : `    got: (${inst.outcome === "errored" ? "errored, with no output captured" : "no result captured in this trace"})`);
  });
  return lines.join("\n");
}

const indent = (s, n = 4) => clean(s).split("\n").map((l) => `${" ".repeat(n)}${l}`).join("\n");

/**
 * A full evidence payload for a batch of candidates.
 *
 * Refuses a candidate with no instances rather than rendering a header with
 * nothing under it: a judge handed an empty section will still return a verdict
 * on it.
 */
export function buildEvidencePayload(candidates, opts = {}) {
  const usable = (candidates || []).filter((c) => (c.instances || []).length);
  const dropped = (candidates || []).length - usable.length;
  if (!usable.length) throw new RangeError("buildEvidencePayload: no candidate carries any instances");
  return {
    text: usable.map((c) => renderCandidate(c, opts)).join("\n\n"),
    rendered: usable.length,
    dropped,
    keys: usable.map((c) => c.act?.key || c.key),
  };
}

/**
 * Attach complete instances to mined acts, from the raw calls they came from.
 *
 * Deliberately a separate step: mining keeps a bounded sample per cluster for
 * memory, and a payload wants the fullest examples available. Prefers instances
 * that HAVE a result, and spreads across sessions so five examples are not five
 * repetitions of one minute of work.
 */
export function pickInstances(instances, { want = 5 } = {}) {
  const withResult = (instances || []).filter((i) => clean(i.output).trim());
  const pool = withResult.length >= Math.min(want, 3) ? withResult : (instances || []);
  const bySession = new Map();
  for (const i of pool) {
    const k = i.sessionId || "";
    if (!bySession.has(k)) bySession.set(k, []);
    bySession.get(k).push(i);
  }
  const queues = [...bySession.values()];
  const out = [];
  for (let round = 0; out.length < want; round += 1) {
    let placed = false;
    for (const q of queues) {
      if (q[round]) { out.push(q[round]); placed = true; if (out.length >= want) break; }
    }
    if (!placed) break;
  }
  return out;
}
